// Estado do formulário de template reutilizável (/disparos/templates).
//
// Um template salvo é a mesma mensagem de um canal de disparo — mesmo
// TemplateDisparo, mesmas regras — só que sem remetente e sem origem de
// contato, que pertencem ao disparo, não ao texto. Por isso o draft carrega
// a mensagem num CanalDraft: assim o TemplateMensagemForm é reaproveitado
// inteiro, sem uma segunda implementação do editor.
//
// Os campos `remetente`, `origemContato` e `fallbacksContato` desse
// CanalDraft ficam vazios de propósito e não aparecem na tela.

import {
  CanalComunicacao,
  CatalogoDisparo,
  ContextoDisparo,
  CriarDisparoTemplateDto,
  DisparoTemplateItem,
  ProvedorComunicacao,
} from "@/models/consignado/disparo.model";
import {
  CanalDraft,
  novoCanal,
  templateParaDto,
  validarMensagem,
  variaveisDoTemplate,
} from "./disparo-draft";
import { semMoldura } from "./blocoEmail";

export interface TemplateDraft {
  nome: string;
  ativo: boolean;
  contexto: ContextoDisparo;
  /** A mensagem. Só canal/provedor/modo/conteúdo/variáveis são usados. */
  mensagem: CanalDraft;
}

export const templateDraftVazio = (): TemplateDraft => ({
  nome: "",
  ativo: true,
  contexto: ContextoDisparo.PROPOSTA,
  mensagem: novoCanal(CanalComunicacao.EMAIL),
});

export const draftDeTemplate = (t: DisparoTemplateItem): TemplateDraft => {
  const base = novoCanal(t.canal);
  return {
    nome: t.nome,
    ativo: t.ativo,
    contexto: t.contexto,
    mensagem: {
      ...base,
      provedor: t.provedor,
      modo: t.conteudo.modo,
      assunto: t.conteudo.assunto ?? "",
      conteudo: semMoldura(t.conteudo.conteudo ?? ""),
      tipoConteudo: t.conteudo.tipoConteudo ?? "TEXTO",
      identificador: t.conteudo.referenciaExterna?.identificador ?? "",
      idioma: t.conteudo.referenciaExterna?.idioma ?? "",
      produtoId: t.conteudo.referenciaExterna?.produtoId ?? "",
      parametros: (t.conteudo.referenciaExterna?.parametros ?? []).map((p, i) => ({
        id: `param-${i}-${p.componente}`,
        componente: p.componente,
        valor: p.valor,
      })),
      variaveis: variaveisDoTemplate(t.conteudo),
    },
  };
};

/**
 * A mensagem de um template reutilizável aplicada ao canal de um disparo,
 * com o vínculo (`templateId`). Remetente e contato ficam como estão — são do
 * disparo, não do template.
 */
export const mensagemDoTemplate = (t: DisparoTemplateItem): Partial<CanalDraft> => {
  const m = draftDeTemplate(t).mensagem;
  return {
    templateId: t.id,
    modo: m.modo,
    assunto: m.assunto,
    conteudo: m.conteudo,
    tipoConteudo: m.tipoConteudo,
    identificador: m.identificador,
    idioma: m.idioma,
    produtoId: m.produtoId,
    parametros: m.parametros,
    variaveis: m.variaveis,
  };
};

export const draftParaTemplateDto = (d: TemplateDraft): CriarDisparoTemplateDto => ({
  nome: d.nome.trim(),
  ativo: d.ativo,
  contexto: d.contexto,
  canal: d.mensagem.canal,
  provedor: d.mensagem.provedor as ProvedorComunicacao,
  template: templateParaDto(d.mensagem),
});

export interface TemplateErros {
  nome?: string;
  mensagem?: string;
}

export const semErrosTemplate = (e: TemplateErros): boolean => !e.nome && !e.mensagem;

/**
 * Mesmas regras que o disparo cobra na mensagem.
 *
 * O backend não as aplica no POST /disparos/templates — só quando o
 * template é usado. Um template salvo com provedor/modo incompatível, ou
 * com variável sem mapeamento, seria aceito ali e recusaria o disparo
 * depois, com um erro longe da causa.
 */
export const validarTemplateDraft = (
  d: TemplateDraft,
  catalogo?: CatalogoDisparo,
): TemplateErros => {
  const erros: TemplateErros = {};
  if (!d.nome.trim()) erros.nome = "Informe um nome.";
  const erroMensagem = validarMensagem(d.mensagem, d.contexto, catalogo);
  if (erroMensagem) erros.mensagem = erroMensagem;
  return erros;
};
